import classNames from "classnames";
import fr from "date-fns/locale/fr";
import MaskedTextInput from "react-text-mask";
import PropTypes from "prop-types";
import React from "react";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getMonth, getYear } from "date-fns";
import { range } from "@utils/arrays";
import { useSession } from "@hooks";
import "react-datepicker/dist/react-datepicker.css";
import ReactDatePicker, {
  ReactDatePickerProps,
  registerLocale,
} from "react-datepicker";
registerLocale("fr", fr);

export interface DatePickerProps extends ReactDatePickerProps {
  additionalClassname?: string;
  selectYearsMonths?: boolean;
  maxYear?: number;
}

/* See documentation https://reactdatepicker.com/ for more informations */
const DatePicker: React.FC<DatePickerProps> = ({
  additionalClassname,
  selectYearsMonths = false,
  maxYear = new Date().getFullYear() + 5,
  ...props
}) => {
  const { getLanguageVarValue } = useSession();

  const defaultClass =
    "tw-border tw-border-gray-300 focus:tw-border-primary-500 tw-outline-none tw-py-1 tw-px-3 tw-leading-8 tw-bg-white tw-transition-colors tw-duration-200 tw-ease-in-out";
  let customClass = defaultClass;
  if (props.className) {
    customClass = props.className;
  } else {
    customClass = classNames(defaultClass, additionalClassname);
  }

  const years = range(1900, maxYear);
  /*const months_EN = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];*/

  const languagesVarsCodes = [
    "LANG_neobooking_january", //Janvier
    "LANG_neobooking_febriary", //Fevrier
    "LANG_neobooking_march", //MArs
    "LANG_neobooking_april", //Avril
    "LANG_neobooking_may", //Mai
    "LANG_neobooking_june", //juin
    "LANG_neobooking_july", //Juillet
    "LANG_neobooking_august", //Aout
    "LANG_neobooking_september", //Sepctembre
    "LANG_neobooking_october", //Octobre
    "LANG_neobooking_november", //Novembre
    "LANG_neobooking_december", //Décembre
    "LANG_neobooking_datepicker_format2_placeholder", //dd/MM/yyyy
  ];

  const months = [
    getLanguageVarValue("LANG_neobooking_january"),
    getLanguageVarValue("LANG_neobooking_febriary"),
    getLanguageVarValue("LANG_neobooking_march"),
    getLanguageVarValue("LANG_neobooking_april"),
    getLanguageVarValue("LANG_neobooking_may"),
    getLanguageVarValue("LANG_neobooking_june"),
    getLanguageVarValue("LANG_neobooking_july"),
    getLanguageVarValue("LANG_neobooking_august"),
    getLanguageVarValue("LANG_neobooking_september"),
    getLanguageVarValue("LANG_neobooking_october"),
    getLanguageVarValue("LANG_neobooking_november"),
    getLanguageVarValue("LANG_neobooking_december"),
  ];

  return (
    <>
      <ReactDatePicker
        customInput={
          <MaskedTextInput
            type="text"
            mask={[/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/]}
          />
        }
        renderCustomHeader={
          selectYearsMonths
            ? ({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="tw-px-6 tw-py-2 tw-flex tw-justify-center tw-gap-x-2">
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    <FontAwesomeIcon
                      className={`${
                        prevMonthButtonDisabled && "tw-opacity-50"
                      }`}
                      icon={faAngleLeft}
                    />
                  </button>
                  <div>
                    <select
                      className="tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-border-l tw-border-t tw-border-b tw-border-gray-400 tw-py-1 tw-px-2 tw-rounded-bl tw-rounded-tl"
                      style={{ paddingRight: "2rem" }}
                      value={getYear(date)}
                      onChange={({ target: { value } }) =>
                        changeYear(parseInt(value))
                      }
                    >
                      {years.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <select
                      className="tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-border-r tw-border-t tw-border-b tw-border-gray-400 tw-py-1 tw-px-2 tw-rounded-br tw-rounded-tr"
                      style={{ paddingRight: "2rem" }}
                      value={months[getMonth(date)]}
                      onChange={({ target: { value } }) =>
                        changeMonth(months.indexOf(value))
                      }
                    >
                      {months.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    <FontAwesomeIcon
                      className={`${
                        nextMonthButtonDisabled && "tw-opacity-50"
                      }`}
                      icon={faAngleRight}
                    />
                  </button>
                </div>
              )
            : undefined
        }
        dayClassName={() => "react-datepicker-day-custom"}
        clearButtonClassName="react-datepicker-clearButton-custom"
        autoComplete="off"
        locale={"fr"}
        className={customClass}
        dateFormat={"dd/MM/yyyy"}
        {...props}
      />
    </>
  );
};

DatePicker.propTypes = {
  additionalClassname: PropTypes.string,
  selectYearsMonths: PropTypes.bool,
  maxYear: PropTypes.number,
};

export default DatePicker;
